import { FC, useState, useEffect, useCallback, useContext, useMemo, Fragment } from 'react'
import {
	Table,
	UfinetBox,
	UfinetSectionBox,
	formatTableFilters,
	UfinetButton,
	MultipleDeleter,
	UfinetModal,
} from 'ufinet-web-components'
import { useModal } from 'ufinet-web-functions'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faDownload, faFileExcel } from '@fortawesome/free-solid-svg-icons'
import { DataTablePFSEvent } from 'primereact/datatable'
import { AuthContext } from 'ufinet-web-functions'
import { useQuery, useMutation } from '@tanstack/react-query'
import BulkUpload from 'components/modals/BulkUpload'
import NewModal from 'components/modals/NewModal'
import { toast } from 'react-toastify'
import { getRows, getExcelFile, deleteRows, getExcelFileTemplate } from 'api/requests'
import { fetchGET2, fetchGETBlob2 } from 'api/https/https'
import { Translation, useTranslation } from 'utils/translation/Translation'
import { getPermissionsByPathname } from 'utils/permissions/getPermissionsByPathname'
import { PathNames, SortOrder } from 'types'
import EditModal from 'components/modals/EditModal'
const NewUfinetBox = UfinetBox as any

type FormattedUserResponse = any

const getFieldName = (columnName: string | string[]) => {
	if (Array.isArray(columnName)) {
		return JSON.stringify(columnName)
	}

	return columnName
}

const getValueFromRow = (row: Record<string, any>, columnName: string | string[]) => {
	if (Array.isArray(columnName)) {
		return columnName.reduce((acc: any, key: string) => {
			if (acc === undefined || acc === null) {
				return undefined
			}
			return acc[key]
		}, row)
	}

	return row[columnName]
}

const renderDiffArray = (value: unknown) => {
	if (!Array.isArray(value) || value.length === 0) {
		return ''
	}

	return (
		<div className="diff-array-body">
			{value.map((item: any, index: number) => (
				<Fragment key={`${item?.field ?? 'field'}-${index}`}>
					<div>
						<strong>{item?.field ?? ''}</strong>
					</div>
					<div>
						<span className="me-2 text-muted">{Translation('PAGE.OLDVALUE')}: </span>
						{item?.oldValue ?? ''}
					</div>
					<div className="mb-2">
						<span className="me-2 text-muted">{Translation('PAGE.NEWVALUE')}: </span>
						{item?.newValue ?? ''}
					</div>
				</Fragment>
			))}
		</div>
	)
}

type SelectFilterOption = {
	label: string
	value: string
}

type DownloadByIdMutation = {
	mutate: (id: string) => void
}

const generateColsData = (
	inputs: any,
	selectFilterOptions: Record<string, SelectFilterOption[]> = {},
	downloadByIdMutation?: DownloadByIdMutation
) => {
	return (
		inputs
			// Delete columns with dontShowInTable = true
			.filter((input: input) => input.dontShowInTableAndModal !== true)
			.map((input: input) => {
				const fieldName = getFieldName(input.columnName)
				const isArrayField = Array.isArray(input.columnName)
				const shouldRenderDiffBody =
					isArrayField || input.columnName === 'diffFields'
				const shouldRenderActionsButton =
					input.columnName === 'actions' && !!downloadByIdMutation

				return {
					field: input.columnName,
					header: Translation(input.columnLabelIntl),
					width: input.columnWidth,
					nonFilterable: input?.nonFilterable ?? false,
					nonSortable:  input?.nonSortable ?? false,
					type: 'text',
					filterType: input?.filterType ?? 'text',
					filterOptions: selectFilterOptions[fieldName],
					body: shouldRenderDiffBody
						? (rowData: Record<string, any>) => renderDiffArray(getValueFromRow(rowData, input.columnName))
						: shouldRenderActionsButton
							? (rowData: Record<string, any>) => (
								<UfinetButton
									content={Translation("BTN.BINNACLE.DETAIL")}
									onClick={() => {
										const rowId = rowData?.id
										if (rowId === undefined || rowId === null) {
											return
										}
										downloadByIdMutation?.mutate(String(rowId))
									}}
								/>
							)
						: undefined,
				}
			})
	)
}

const statusServicesOptions = [
		{
			label: 'ACTIVE',
			value: 'ACTIVE'
		},
		{
			label: 'INACTIVE',
			value: 'INACTIVE'
		},
		{
			label: 'RESERVED',
			value: 'RESERVED'
		},
	]


export interface input {
	columnName: string | string[]
	columnLabelIntl: string
	columnWidth: string
	columnType: string
	requiredFieldErrorText: string
	dontShowInTableAndModal?: boolean
	nonFilterable?: boolean
	nonSortable?: boolean
	filterType?: any
	customBodyType?: 'actionsButton'
}

type StandardTableWithButtonsProps = {
	menuGroupedBy: string
	initialSorting: {
		sortField: string
		sortOrder: string
	} | {};
	inputs: input[]
	pathName: string
	reactQueryKeyName: string
	apiRoutePath: string
	apiMutationRoutePath?: string
	downloadedFileName: string
	addModalTittleIntl: string
	editModalTittleIntl: string
	onlyViewMode: boolean | undefined
	menuTittle: string
}

const StandardTableWithButtons: FC<StandardTableWithButtonsProps> = ({
	menuGroupedBy,
	initialSorting,
	inputs,
	pathName,
	reactQueryKeyName,
	apiRoutePath,
	apiMutationRoutePath,
	downloadedFileName,
	addModalTittleIntl,
	editModalTittleIntl,
	onlyViewMode,
	menuTittle,
}) => {
	const authData = useContext(AuthContext)
	const roles = authData.userData?.authorities || []
	const permissions: any = getPermissionsByPathname(pathName, roles)


	const translation = useTranslation()


	const isServices = pathName.toLocaleLowerCase() === PathNames.SERVICES

	
	const isBitacora = pathName?.toLowerCase() === PathNames.BITACORA
	const isBitacoraMasive = pathName?.toLowerCase() === PathNames.BITACORA_MASIVE
	const hasTipoSelect =
		(isBitacora || isBitacoraMasive) &&
		inputs.some((input) => input.columnName === 'tipo' && input.filterType === 'select')

	const hasTipoMasiveSelect =
		(isBitacoraMasive) &&
		inputs.some((input) => input.columnName === 'tipo' && input.filterType === 'select')

	const hasUsuarioSelect =
		(isBitacora || isBitacoraMasive) &&
		inputs.some((input) => input.columnName === 'usuario' && input.filterType === 'select')

	const hasUsuarioSelectMasive =
		isBitacoraMasive &&
		inputs.some((input) => input.columnName === 'usuario' && input.filterType === 'select')
		
	const hasTablaSelect =
		(isBitacora || isBitacoraMasive) &&
		inputs.some((input) => input.columnName === 'tabla' && input.filterType === 'select')

	const hasTablaMasiveSelect =
		(isBitacoraMasive) &&
		inputs.some((input) => input.columnName === 'tabla' && input.filterType === 'select')

	const hasStatusServices = 
		isServices &&
		inputs.some((input) => input.columnName === 'status' && input.filterType === 'select')

	const fetchSelectOptions = async (endpoint: string) => {
		if (!apiRoutePath) {
			return []
		}
		const route =
			apiRoutePath === 'Bitacora/masive' ? endpoint : `${apiRoutePath}/${endpoint}`
		const response = await fetchGET2(route)
		if (!Array.isArray(response?.data)) {
			return []
		}

		return response.data.map((item: any) => {

			return {
				label: item,
				value: item,
			}
		})
	}

	const typeListFiltersQuery = useQuery({
		queryKey: [apiRoutePath, 'tipolist'],
		queryFn: () => fetchSelectOptions('tipolist'),
		enabled: isBitacora,
	})

	const typeListMasiveFiltersQuery = useQuery({
		queryKey: [apiRoutePath, 'tipolistmasive'],
		queryFn: () => fetchSelectOptions('Bitacora/tipolistmasive'),
		enabled: isBitacoraMasive,
	})

	const usuarioListFiltersQuery = useQuery({
		queryKey: [apiRoutePath, 'usuariolist'],
		queryFn: () => fetchSelectOptions('usuariolist'),
		enabled: isBitacora,
	})

	const usuarioMasiveListFiltersQuery = useQuery({
		queryKey: [apiRoutePath, 'usuariolistMavise'],
		queryFn: () => fetchSelectOptions('Bitacora/usuariolistmasive'),
		enabled: isBitacoraMasive,
	})

	const tablaListFiltersQuery = useQuery({
		queryKey: [apiRoutePath, 'tablalist'],
		queryFn: () => fetchSelectOptions('tablalist'),
		enabled: isBitacora,
	})


	const tablaListMasiveFiltersQuery = useQuery({
		queryKey: [apiRoutePath, 'tablalistmasive'],
		queryFn: () => fetchSelectOptions('Bitacora/tablalistmasive'),
		enabled: isBitacoraMasive,
	})

	const selectFilterOptions = useMemo<Record<string, SelectFilterOption[]>>(() => {
		if (!isBitacora && !isBitacoraMasive && !isServices) {
			return {}
		}

		const options: Record<string, any[]> = {}

		if (hasTipoSelect && Array.isArray(typeListFiltersQuery.data)) {
			options.tipo = typeListFiltersQuery.data
		}
		
		if (hasTipoMasiveSelect && Array.isArray(typeListMasiveFiltersQuery.data)) {
			options.tipo = typeListMasiveFiltersQuery.data
		}

		if (hasUsuarioSelect && Array.isArray(usuarioListFiltersQuery.data)) {
			options.usuario = usuarioListFiltersQuery.data
		}

		if (hasUsuarioSelectMasive && Array.isArray(usuarioMasiveListFiltersQuery.data)) {
			options.usuario = usuarioMasiveListFiltersQuery.data
		}

		if (hasTablaSelect && Array.isArray(tablaListFiltersQuery.data)) {
			options.tabla = tablaListFiltersQuery.data
		}
		
		if (hasStatusServices && Array.isArray(statusServicesOptions)) {
			options.status = statusServicesOptions
		}

		if (hasTablaMasiveSelect && Array.isArray(tablaListMasiveFiltersQuery.data)) {
			options.tabla = tablaListMasiveFiltersQuery.data
		}

		return options
	}, [
		isBitacora,
		isServices,
		isBitacoraMasive,
		hasTipoSelect,
		hasTipoMasiveSelect,
		hasUsuarioSelect,
		hasUsuarioSelectMasive,
		hasTablaSelect,
		hasStatusServices,
		typeListFiltersQuery.data,
		usuarioListFiltersQuery.data,
		tablaListFiltersQuery.data,
		statusServicesOptions,
		hasTablaMasiveSelect,
		typeListFiltersQuery.data,
		usuarioListFiltersQuery.data,
		tablaListFiltersQuery.data,
		usuarioMasiveListFiltersQuery.data,
		tablaListMasiveFiltersQuery.data,
		typeListMasiveFiltersQuery.data,
	])

	

	const noShowGlobalFilterArray = ['CTO', 'POLYGON', 'PFS',]
	

	const initialFilterData = {
		pageNumber: 0,
		rowsPerPage: 20,
	}


	const initialState = {
		...initialFilterData,
		...initialSorting
	}

	const [selectedValues, setSelectedValues] = useState<FormattedUserResponse[]>([])
	const [paging, setPaging] = useState<any>()
	const [filterData, setFilterData] = useState<Record<string, any>>(initialState)

	const [modalRow, setModalRow] = useState<any>({})



const hasChanged = (currentState: object) =>
  JSON.stringify(currentState) !== JSON.stringify(initialState)



	const query = useQuery({
		queryKey: [reactQueryKeyName, filterData],
		queryFn: () => getRows(filterData, apiRoutePath),
	})
	const { data, isPending, isLoading, isError, error } = query


	const tableContent = useMemo(() => {
		if (!data?.data) {
			return []
		}

		return data.data.map((item: Record<string, any>) => {
			const formattedItem: Record<string, any> = { ...item }

			inputs.forEach((input) => {
				if (Array.isArray(input.columnName)) {
					const fieldName = getFieldName(input.columnName)
					const fieldValue = getValueFromRow(item, input.columnName)

					formattedItem[fieldName] = fieldValue
				}
			})

			return formattedItem
		})
	}, [data?.data, inputs])

	const totalExcelMutation = useMutation({
		mutationFn: () => {
			const {rowsPerPage, pageNumber, ...dataWithOutRowsPerPage} = filterData


			return getExcelFile(
				dataWithOutRowsPerPage,
				apiRoutePath,
				downloadedFileName
			)

		}
		
	});


	const getFileMutation = useMutation({
		mutationFn: () => {
			return getExcelFileTemplate(apiRoutePath, downloadedFileName)
		}
	})
  
  const {
	isPending: isTotalExcelPending,
	isError: isExcelError,
	error: excelError,
  } = totalExcelMutation;






	const resolveBlobErrorMessage = async (error: any) => {
		const responseData = error?.response?.data
		if (responseData && typeof responseData?.text === 'function') {
			try {
				const text = await responseData.text()
				try {
					const json = JSON.parse(text)
					return json?.message || json?.detail || json?.title || text
				} catch {
					return text
				}
			} catch {
				return undefined
			}
		}

		return (
			responseData?.message ||
			responseData?.detail ||
			responseData?.title ||
			error?.[0] ||
			error?.message
		)
	}

	const downloadByIdMutation = useMutation({
		mutationFn: (id: string) => {
			return fetchGETBlob2(
				`${apiMutationRoutePath}`,
				{ params: { id } },
				downloadedFileName
			)
		},
		onError: (error: any) => {
			void resolveBlobErrorMessage(error).then((message) => {
				toast.error(message || genericErrorMessage)
			})
		},
	})

	const selectFieldsUnauthorized =
		(hasTipoSelect &&
			typeListFiltersQuery.isError &&
			(typeListFiltersQuery.error as any)?.response?.status === 401) ||
		(hasUsuarioSelect &&
			usuarioListFiltersQuery.isError &&
			(usuarioListFiltersQuery.error as any)?.response?.status === 401) ||
		(hasTablaSelect &&
			tablaListFiltersQuery.isError &&
			(tablaListFiltersQuery.error as any)?.response?.status === 401)


	// Logout user if error 401
	const unauthorizedError = Translation('AUTH.UNAUTHORIZED')
	useEffect(() => {
		const mainQueryUnauthorized = isError && (error as any)?.response?.status === 401
		const excelUnauthorized = isExcelError && (excelError as any)?.response?.status === 401

		if (mainQueryUnauthorized || excelUnauthorized || selectFieldsUnauthorized) {
			toast.error(unauthorizedError)
			authData.logout()
		}
	}, [
		isError,
		error,
		isExcelError,
		excelError,
		selectFieldsUnauthorized,
		authData,
		unauthorizedError,
	])

	// Mutation to handle delete rows
	const genericErrorMessage = Translation('MODALS.GENERICERROR')
	const successMessage = Translation('PAGE.ROWSDELETED')
	const mutation = useMutation({
		mutationFn: (values: any) => deleteRows(values, apiRoutePath),
		onSuccess: () => {
			toast.success(successMessage)
		},
		onError: (error: any) => {
			if (error.response.status === 401) {
				toast.error(unauthorizedError)
				authData.logout()
				return
			}
			toast.error(
				error?.response?.data?.message ||
					error?.response?.data?.detail ||
					error?.response?.data?.title ||
					genericErrorMessage
			)
		},
	})


	const { mutateAsync: deleteRowsMutation } = mutation

	const [newModal, showNewModal, hideNewModal] = useModal()
	const [editModal, showEditModal, hideEditModal] = useModal()
	const [bulkUploadModal, showBulkUploadModal, hideBulkUploadModal] = useModal()

	useEffect(() => {
		if (data) {
			const parsePagination = JSON.parse(data?.headers['x-pagination'])
			const formattedPagination = {
				totalElements: parsePagination.totalCount,
				totalPages: parsePagination.totalPages,
				pageSize: parsePagination.rowsPerPage,
				pageNumber: parsePagination.currentPage,
			}
			setPaging(formattedPagination)
		}
	}, [data])

	useEffect(() => {

		setFilterData({
			...initialFilterData,
			...initialSorting
		})

	}, [pathName,initialSorting])

	useEffect(() => {
		// Clean component on unmount (changes pathname)
		return () => {
			setSelectedValues([])
			setPaging(undefined)
			setFilterData({
				...initialFilterData,
				...initialSorting
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathName])

	const onFilterClear = (): void => {
		setFilterData({
			...initialFilterData,
			...initialSorting
		})
	}

	const onFilter = (e: DataTablePFSEvent): void => {
		const formattedFilters = formatTableFilters(e)
		
		
		
		setFilterData({
			...formattedFilters,
			...initialSorting,
		})
	}

	const onPage = (e: DataTablePFSEvent): void => {
		setFilterData({ ...filterData, pageNumber: e.page, rowsPerPage: e.rows })
	}

	const onSort = (e: DataTablePFSEvent): void => {
		
		if(e.sortField === null) {
			return setFilterData({
				...filterData,
				...initialSorting,
			})
		}

		setFilterData({
			...filterData,
			sortField: e.sortField,
			sortOrder: e.sortOrder === 1 ? SortOrder.ASC : e.sortOrder === -1 ? SortOrder.DESC : null,
		})
	}


	const getHeaderButtons = () => {
		return (
			<>
				{!onlyViewMode && (
					<>
						{permissions.canDelete && selectedValues.length > 0 && (
							<MultipleDeleter
								setSelectedValues={setSelectedValues}
								selectedValues={selectedValues!}
								deleteEndpoint={() => deleteRowsMutation(selectedValues)}
								search={query.refetch}
							/>
						)}

						{permissions.canWrite && (
							<>
								<UfinetButton
									className="me-3"
									icon={faPlus}
									onClick={() => showNewModal()}
									content={Translation('PAGE.NEW')}
								/>
								{pathName !== 'Operator' && (
									<UfinetButton
										className="me-3"
										icon={faPlus}
										onClick={() => showBulkUploadModal()}
										content={Translation('PAGE.MASSIVE')}
									/>
								)}
							</>
						)}
					</>
				)}
				{
					PathNames.BITACORA_MASIVE !== pathName && PathNames.BITACORA !== pathName && 
				 <UfinetButton
					className="me-3"
					icon={faFileExcel}
					isDisabled={getFileMutation.isPending}
					onClick={() => getFileMutation.mutate()}
					content={Translation('PAGE.DOWNLOADTEMPLATE')}
				/>}

				{
					PathNames.BITACORA_MASIVE !== pathName &&
					<UfinetButton
						className="me-3"
						icon={faDownload}
						isDisabled={isTotalExcelPending}
						onClick={() => totalExcelMutation.mutate()}
						content={PathNames.BITACORA === pathName ? Translation('PAGE.DOWNLOADREGISTRY.BINNACLE') : isTotalExcelPending ? Translation('LOADING_DOTS') : Translation('PAGE.DOWNLOADREGISTRY')}
					/>
				}
			</>
		)
	}

	function getEditPencilBodyTemplate<T>(rowData: T, onClick: () => void) {
		if (pathName === 'PolygonOptimization') {
			return <></>
		}
		return (
			<>
				{!onlyViewMode && (
					<div>
						<i className="pi pi-pencil edit-pencil" onClick={onClick} />
					</div>
				)}
			</>
		)
	}

	const onSelectionChange = useCallback((values: FormattedUserResponse[]) => setSelectedValues(values), [])

	const cols = useMemo(
		() =>
			generateColsData(
				inputs,
				selectFilterOptions,
				pathName === PathNames.BITACORA_MASIVE ? downloadByIdMutation : undefined
			),
		[inputs, selectFilterOptions, pathName, downloadByIdMutation]
	)

	return (
		<>
			<NewUfinetBox>
				<UfinetSectionBox>
					<h2 className="fs-3 fw-bolder text-dark position-relative">{Translation(menuTittle)}</h2>
					<Table
						enableSearchFilter={!noShowGlobalFilterArray.includes(menuGroupedBy)}
						rowsPerPageOptions={[10, 50, 100, 500, 1000]}
						key={apiRoutePath}
						dataKey="uniqueId"
						selectedValues={!onlyViewMode ? selectedValues : []}
						onSelectionChange={permissions.canDelete && !onlyViewMode ? onSelectionChange : undefined}
						cols={cols}
						content={tableContent}
						headerButtons={getHeaderButtons()}
						actionBodyTemplate={
							!onlyViewMode && permissions.canUpdate
								? (row: string) =>
										getEditPencilBodyTemplate(row, () => {
											setModalRow(row)
											showEditModal()
										})
								: undefined
						}
						emptyMessage={(isPending ||  isLoading ) ? Translation('LOADING_DOTS') : (data?.data.length === 0) ? translation('NO.DATA.TABLE') : undefined}
						onFilterClear={onFilterClear}
						onSort={onSort}
						lazySettings={
							paging && {
								...paging,
								loading: isPending || isLoading,
								onFilter,
								onPage,
								onSort,
							}
						}
					/>

					<UfinetModal
						size="lg"
						show={newModal}
						handleClose={() => {
							hideNewModal()
							setModalRow({})
						}}
						title={
							Object.keys(modalRow).length
								? Translation(editModalTittleIntl) ?? ''
								: Translation(addModalTittleIntl) ?? ''
						}
					>
						<NewModal
							hideModal={hideNewModal}
							apiRoutePath={apiRoutePath}
							row={modalRow}
							successFn={() => setModalRow({})}
							inputs={inputs}
							toInvalidateQuery={reactQueryKeyName}
							onSuccessAddMessage={Translation('MODALS.ADDSUCCESS') || ''}
							onSuccessEditMessage={Translation('MODALS.EDITSUCCESS') || ''}
							onErrorGenericMessage={Translation('MODALS.GENERICERROR') || ''}
						/>
					</UfinetModal>
					<UfinetModal
						size="lg"
						show={editModal}
						handleClose={() => {
							hideEditModal()
							setModalRow({})
						}}
						title={
							Object.keys(modalRow).length
								? Translation(editModalTittleIntl) ?? ''
								: Translation(addModalTittleIntl) ?? ''
						}
					>
						<EditModal
							hideModal={hideEditModal}
							apiRoutePath={apiRoutePath}
							row={modalRow}
							successFn={() => setModalRow({})}
							inputs={inputs}
							toInvalidateQuery={reactQueryKeyName}
							onSuccessAddMessage={Translation('MODALS.ADDSUCCESS') || ''}
							onSuccessEditMessage={Translation('MODALS.EDITSUCCESS') || ''}
							onErrorGenericMessage={Translation('MODALS.GENERICERROR') || ''}
						/>
					</UfinetModal>
					<UfinetModal
						size="lg"
						show={bulkUploadModal}
						handleClose={hideBulkUploadModal}
						title={Translation('PAGE.MASSIVE') || ''}
					>
						<BulkUpload
							hideModal={hideBulkUploadModal}
							apiRoutePath={apiRoutePath}
							toInvalidateQuery={reactQueryKeyName}
						/>
					</UfinetModal>
				</UfinetSectionBox>
			</NewUfinetBox>
		</>
	)
}

export default StandardTableWithButtons
